import { Dialog, Transition } from "@headlessui/react";
import { useFormik } from "formik";
import React, { Fragment, useEffect, useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import { IoMdClose } from "react-icons/io";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  useGetDataVariationsQuery,
  useGetTVVariationsQuery,
  usePreviewAirtimeMutation,
  usePreviewDataMutation,
  usePreviewElectricityMutation,
  usePreviewTVMutation,
  usePreviewEPinsMutation,
  usePreviewBettingMutation,
  useConfirmAirtimeMutation,
  useConfirmDataMutation,
  useConfirmElectricityMutation,
  useConfirmTVMutation,
  useConfirmEPinsMutation,
  useConfirmBettingMutation,
  PreviewResponse,
} from "../../../features/ebills/ebillsSlice";

interface BillFormProps {
  isFormModalOpen: boolean;
  setIsFormModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  category: string;
  name: string;
}

// Service IDs
const AIRTIME_SERVICES = [
  { value: "mtn", label: "MTN" },
  { value: "airtel", label: "Airtel" },
  { value: "glo", label: "Glo" },
  { value: "9mobile", label: "9mobile" },
];

const DATA_SERVICES = [
  { value: "mtn", label: "MTN" },
  { value: "airtel", label: "Airtel" },
  { value: "glo", label: "Glo" },
  { value: "9mobile", label: "9mobile" },
  { value: "smile", label: "Smile" },
];

const ELECTRICITY_SERVICES = [
  { value: "ikeja-electric", label: "Ikeja Electric" },
  { value: "eko-electric", label: "Eko Electric" },
  { value: "kano-electric", label: "Kano Electric" },
  { value: "portharcourt-electric", label: "Port Harcourt Electric" },
  { value: "jos-electric", label: "Jos Electric" },
  { value: "ibadan-electric", label: "Ibadan Electric" },
  { value: "kaduna-electric", label: "Kaduna Electric" },
  { value: "abuja-electric", label: "Abuja Electric" },
  { value: "enugu-electric", label: "Enugu Electric" },
  { value: "benin-electric", label: "Benin Electric" },
  { value: "aba-electric", label: "Aba Electric" },
  { value: "yola-electric", label: "Yola Electric" },
];

const METER_TYPES = [
  { value: "prepaid", label: "Prepaid" },
  { value: "postpaid", label: "Postpaid" },
];

const TV_SERVICES = [
  { value: "dstv", label: "DStv" },
  { value: "gotv", label: "GOtv" },
  { value: "startimes", label: "Startimes" },
  { value: "showmax", label: "Showmax" },
];

const SUBSCRIPTION_TYPES = [
  { value: "", label: "No subscription type" },
  { value: "renew", label: "Renew" },
  { value: "change", label: "Change" },
];

const EPINS_SERVICES = [
  { value: "mtn", label: "MTN" },
  { value: "airtel", label: "Airtel" },
  { value: "glo", label: "Glo" },
  { value: "9mobile", label: "9mobile" },
];

const EPINS_VALUES = [100, 200, 500];

const BETTING_SERVICES = [
  { value: "1xBet", label: "1xBet" },
  { value: "BangBet", label: "BangBet" },
  { value: "Bet9ja", label: "Bet9ja" },
  { value: "BetKing", label: "BetKing" },
  { value: "BetLand", label: "BetLand" },
  { value: "BetLion", label: "BetLion" },
  { value: "BetWay", label: "BetWay" },
  { value: "CloudBet", label: "CloudBet" },
  { value: "LiveScoreBet", label: "LiveScoreBet" },
  { value: "MerryBet", label: "MerryBet" },
  { value: "NaijaBet", label: "NaijaBet" },
  { value: "NairaBet", label: "NairaBet" },
  { value: "SupaBet", label: "SupaBet" },
];

// Phone validation regex
const phoneRegex = /^(\+234|0)[0-9]{10}$/;

// Validation schemas
const getValidationSchema = (category: string) => {
  if (category === "airtime") {
    return Yup.object().shape({
      service_id: Yup.string().required("Service provider is required"),
      phone: Yup.string()
        .matches(phoneRegex, "Invalid phone number format. Use +234 or 0 prefix")
        .required("Phone number is required"),
      amount: Yup.number()
        .positive("Amount must be positive")
        .required("Amount is required")
        .test("min-amount", "Amount too low", function (value) {
          if (!value) return false;
          const { service_id } = this.parent;
          if (service_id === "mtn") {
            return value >= 10;
          }
          return value >= 50;
        }),
      acceptTerms: Yup.boolean().oneOf([true], "You must accept the terms"),
    });
  }

  if (category === "data") {
    return Yup.object().shape({
      service_id: Yup.string().required("Service provider is required"),
      variation_id: Yup.string().required("Data plan is required"),
      phone: Yup.string()
        .matches(phoneRegex, "Invalid phone number format. Use +234 or 0 prefix")
        .required("Phone number is required"),
      acceptTerms: Yup.boolean().oneOf([true], "You must accept the terms"),
    });
  }

  if (category === "electricity") {
    return Yup.object().shape({
      service_id: Yup.string().required("Electricity provider is required"),
      variation_id: Yup.string().required("Meter type is required"),
      customer_id: Yup.string()
        .required("Meter number is required")
        .min(10, "Meter number must be at least 10 characters"),
      amount: Yup.number()
        .positive("Amount must be positive")
        .required("Amount is required")
        .min(100, "Minimum amount is ₦100"),
      acceptTerms: Yup.boolean().oneOf([true], "You must accept the terms"),
    });
  }

  if (category === "tv") {
    return Yup.object().shape({
      service_id: Yup.string().required("Service provider is required"),
      variation_id: Yup.string().required("Package is required"),
      customer_id: Yup.string()
        .required("Customer ID is required")
        .min(10, "Customer ID must be at least 10 characters"),
      subscription_type: Yup.string(),
      tv_amount: Yup.number().when("subscription_type", {
        is: "renew",
        then: (schema) => schema.required("Renewal amount is required").positive("Amount must be positive"),
        otherwise: (schema) => schema.notRequired(),
      }),
      acceptTerms: Yup.boolean().oneOf([true], "You must accept the terms"),
    });
  }

  if (category === "epins") {
    return Yup.object().shape({
      service_id: Yup.string().required("Service provider is required"),
      value: Yup.number()
        .oneOf(EPINS_VALUES, "Value must be 100, 200, or 500")
        .required("Value is required"),
      quantity: Yup.number()
        .integer("Quantity must be a whole number")
        .min(1, "Minimum quantity is 1")
        .max(40, "Maximum quantity is 40")
        .required("Quantity is required"),
      acceptTerms: Yup.boolean().oneOf([true], "You must accept the terms"),
    });
  }

  if (category === "betting") {
    return Yup.object().shape({
      service_id: Yup.string().required("Betting provider is required"),
      customer_id: Yup.string()
        .required("Customer ID is required")
        .min(1, "Customer ID is required"),
      amount: Yup.number()
        .positive("Amount must be positive")
        .required("Amount is required")
        .min(100, "Minimum amount is ₦100")
        .max(100000, "Maximum amount is ₦100,000"),
      acceptTerms: Yup.boolean().oneOf([true], "You must accept the terms"),
    });
  }

  return Yup.object().shape({});
};

export const BillForm = ({
  isFormModalOpen,
  setIsFormModalOpen,
  category,
  name,
}: BillFormProps) => {
  const [previewData, setPreviewData] = useState<PreviewResponse | null>(null);
  const [selectedServiceId, setSelectedServiceId] = useState<string>("");
  const [lastPreviewValues, setLastPreviewValues] = useState<any>(null);

  // Fetch data variations when service_id changes for data category
  const {
    data: variationsData,
    isLoading: isLoadingVariations,
    error: variationsError,
  } = useGetDataVariationsQuery(selectedServiceId, {
    skip: category !== "data" || !selectedServiceId,
  });

  // Fetch TV variations when service_id changes for TV category
  const {
    data: tvVariationsData,
    isLoading: isLoadingTVVariations,
    error: tvVariationsError,
  } = useGetTVVariationsQuery(selectedServiceId, {
    skip: category !== "tv" || !selectedServiceId,
  });

  const [previewAirtime, { isLoading: isPreviewingAirtime }] =
    usePreviewAirtimeMutation();
  const [previewDataMutation, { isLoading: isPreviewingData }] =
    usePreviewDataMutation();
  const [previewElectricity, { isLoading: isPreviewingElectricity }] =
    usePreviewElectricityMutation();
  const [previewTV, { isLoading: isPreviewingTV }] = usePreviewTVMutation();
  const [previewEPins, { isLoading: isPreviewingEPins }] =
    usePreviewEPinsMutation();
  const [previewBetting, { isLoading: isPreviewingBetting }] =
    usePreviewBettingMutation();

  const [confirmAirtime, { isLoading: isConfirmingAirtime }] =
    useConfirmAirtimeMutation();
  const [confirmData, { isLoading: isConfirmingData }] =
    useConfirmDataMutation();
  const [confirmElectricity, { isLoading: isConfirmingElectricity }] =
    useConfirmElectricityMutation();
  const [confirmTV, { isLoading: isConfirmingTV }] = useConfirmTVMutation();
  const [confirmEPins, { isLoading: isConfirmingEPins }] =
    useConfirmEPinsMutation();
  const [confirmBetting, { isLoading: isConfirmingBetting }] =
    useConfirmBettingMutation();

  const isPreviewing =
    isPreviewingAirtime ||
    isPreviewingData ||
    isPreviewingElectricity ||
    isPreviewingTV ||
    isPreviewingEPins ||
    isPreviewingBetting;

  const isConfirming =
    isConfirmingAirtime ||
    isConfirmingData ||
    isConfirmingElectricity ||
    isConfirmingTV ||
    isConfirmingEPins ||
    isConfirmingBetting;

  // Reset form and preview when modal closes or category changes
  useEffect(() => {
    if (!isFormModalOpen) {
      setPreviewData(null);
      setSelectedServiceId("");
      setLastPreviewValues(null);
    }
  }, [isFormModalOpen]);

  // Get initial values based on category
  const getInitialValues = () => {
    if (category === "airtime") {
      return {
        service_id: "",
        phone: "",
        amount: 0,
        acceptTerms: false,
      };
    }
    if (category === "data") {
      return {
        service_id: "",
        variation_id: "",
        phone: "",
        acceptTerms: false,
      };
    }
    if (category === "electricity") {
      return {
        service_id: "",
        variation_id: "",
        customer_id: "",
        amount: 0,
        acceptTerms: false,
      };
    }
    if (category === "tv") {
      return {
        service_id: "",
        variation_id: "",
        customer_id: "",
        subscription_type: "",
        tv_amount: 0,
        acceptTerms: false,
      };
    }
    if (category === "epins") {
      return {
        service_id: "",
        value: 100,
        quantity: 1,
        acceptTerms: false,
      };
    }
    if (category === "betting") {
      return {
        service_id: "",
        customer_id: "",
        amount: 0,
        acceptTerms: false,
      };
    }
    return {
      acceptTerms: false,
    };
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: getValidationSchema(category),
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        if (category === "airtime") {
          const airtimeValues = values as {
            phone: string;
            service_id: string;
            amount: number;
          };
          const response = await previewAirtime({
            product: "airtime",
            phone: airtimeValues.phone,
            service_id: airtimeValues.service_id,
            amount: airtimeValues.amount,
          }).unwrap();
          setPreviewData(response);
          setLastPreviewValues(formik.values);
          toast.success("Preview generated successfully");
        } else if (category === "data") {
          const dataValues = values as {
            phone: string;
            service_id: string;
            variation_id: string;
          };
          const response = await previewDataMutation({
            product: "data",
            phone: dataValues.phone,
            service_id: dataValues.service_id,
            variation_id: dataValues.variation_id,
          }).unwrap();
          setPreviewData(response);
          setLastPreviewValues(formik.values);
          toast.success("Preview generated successfully");
        } else if (category === "electricity") {
          const electricityValues = values as {
            customer_id: string;
            service_id: string;
            variation_id: string;
            amount: number;
          };
          const response = await previewElectricity({
            product: "electricity",
            customer_id: electricityValues.customer_id,
            service_id: electricityValues.service_id,
            variation_id: electricityValues.variation_id,
            amount: electricityValues.amount,
          }).unwrap();
          setPreviewData(response);
          setLastPreviewValues(formik.values);
          toast.success("Preview generated successfully");
        } else if (category === "tv") {
          const tvValues = values as {
            customer_id: string;
            service_id: string;
            variation_id: string;
            subscription_type?: string;
            tv_amount?: number;
          };
          const requestBody: any = {
            product: "tv",
            customer_id: tvValues.customer_id,
            service_id: tvValues.service_id,
            variation_id: tvValues.variation_id,
          };
          if (tvValues.subscription_type) {
            requestBody.subscription_type = tvValues.subscription_type;
            if (tvValues.subscription_type === "renew") {
              // Append "t" to variation_id for renew
              requestBody.variation_id = tvValues.variation_id + "t";
              if (tvValues.tv_amount) {
                requestBody.tv_amount = tvValues.tv_amount;
              }
            }
          }
          const response = await previewTV(requestBody).unwrap();
          setPreviewData(response);
          setLastPreviewValues(formik.values);
          toast.success("Preview generated successfully");
        } else if (category === "epins") {
          const epinsValues = values as {
            service_id: string;
            value: number;
            quantity: number;
          };
          const response = await previewEPins({
            product: "epins",
            service_id: epinsValues.service_id,
            value: epinsValues.value,
            quantity: epinsValues.quantity,
          }).unwrap();
          setPreviewData(response);
          setLastPreviewValues(formik.values);
          toast.success("Preview generated successfully");
        } else if (category === "betting") {
          const bettingValues = values as {
            customer_id: string;
            service_id: string;
            amount: number;
          };
          const response = await previewBetting({
            product: "betting",
            customer_id: bettingValues.customer_id,
            service_id: bettingValues.service_id,
            amount: bettingValues.amount,
          }).unwrap();
          setPreviewData(response);
          setLastPreviewValues(formik.values);
          toast.success("Preview generated successfully");
        }
      } catch (error: any) {
        toast.error(error?.data?.message || "Failed to generate preview");
        console.error("Preview error:", error);
      }
    },
  });

  // Handle confirm
  const handleConfirm = async () => {
    if (!previewData || !lastPreviewValues) return;

    try {
      if (category === "airtime") {
        const airtimeValues = lastPreviewValues as {
          phone: string;
          service_id: string;
          amount: number;
        };
        await confirmAirtime({
          product: "airtime",
          phone: airtimeValues.phone,
          service_id: airtimeValues.service_id,
          amount: airtimeValues.amount,
        }).unwrap();
        toast.success("Transaction confirmed successfully");
        setIsFormModalOpen(false);
      } else if (category === "data") {
        const dataValues = lastPreviewValues as {
          phone: string;
          service_id: string;
          variation_id: string;
        };
        await confirmData({
          product: "data",
          phone: dataValues.phone,
          service_id: dataValues.service_id,
          variation_id: dataValues.variation_id,
        }).unwrap();
        toast.success("Transaction confirmed successfully");
        setIsFormModalOpen(false);
      } else if (category === "electricity") {
        const electricityValues = lastPreviewValues as {
          customer_id: string;
          service_id: string;
          variation_id: string;
          amount: number;
        };
        await confirmElectricity({
          product: "electricity",
          customer_id: electricityValues.customer_id,
          service_id: electricityValues.service_id,
          variation_id: electricityValues.variation_id,
          amount: electricityValues.amount,
        }).unwrap();
        toast.success("Transaction confirmed successfully");
        setIsFormModalOpen(false);
      } else if (category === "tv") {
        const tvValues = lastPreviewValues as {
          customer_id: string;
          service_id: string;
          variation_id: string;
          subscription_type?: string;
          tv_amount?: number;
        };
        const requestBody: any = {
          product: "tv",
          customer_id: tvValues.customer_id,
          service_id: tvValues.service_id,
          variation_id: tvValues.variation_id,
        };
        if (tvValues.subscription_type) {
          requestBody.subscription_type = tvValues.subscription_type;
          if (tvValues.subscription_type === "renew") {
            requestBody.variation_id = tvValues.variation_id + "t";
            if (tvValues.tv_amount) {
              requestBody.tv_amount = tvValues.tv_amount;
            }
          }
        }
        await confirmTV(requestBody).unwrap();
        toast.success("Transaction confirmed successfully");
        setIsFormModalOpen(false);
      } else if (category === "epins") {
        const epinsValues = lastPreviewValues as {
          service_id: string;
          value: number;
          quantity: number;
        };
        await confirmEPins({
          product: "epins",
          service_id: epinsValues.service_id,
          value: epinsValues.value,
          quantity: epinsValues.quantity,
        }).unwrap();
        toast.success("Transaction confirmed successfully");
        setIsFormModalOpen(false);
      } else if (category === "betting") {
        const bettingValues = lastPreviewValues as {
          customer_id: string;
          service_id: string;
          amount: number;
        };
        await confirmBetting({
          product: "betting",
          customer_id: bettingValues.customer_id,
          service_id: bettingValues.service_id,
          amount: bettingValues.amount,
        }).unwrap();
        toast.success("Transaction confirmed successfully");
        setIsFormModalOpen(false);
      }
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to confirm transaction");
      console.error("Confirm error:", error);
    }
  };

  // Handle service_id change for data and TV categories
  useEffect(() => {
    if ((category === "data" || category === "tv") && formik.values.service_id) {
      setSelectedServiceId(formik.values.service_id);
      formik.setFieldValue("variation_id", "");
    }
  }, [formik.values.service_id, category]);

  // Check if form values have changed since last preview
  useEffect(() => {
    if (previewData && lastPreviewValues) {
      const currentValues = JSON.stringify(formik.values);
      const lastValues = JSON.stringify(lastPreviewValues);
      if (currentValues !== lastValues) {
        // Form values changed, hide preview
        setPreviewData(null);
      }
    }
  }, [formik.values, previewData, lastPreviewValues]);

  // Format phone number to include +234 if starts with 0
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Auto-format: if starts with 0 and has 11 digits, convert to +234
    if (value.startsWith("0") && value.length === 11) {
      value = "+234" + value.substring(1);
    }
    formik.setFieldValue("phone", value);
  };

  const handleServiceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    formik.setFieldValue("service_id", e.target.value);
    if (category === "data" || category === "tv") {
      formik.setFieldValue("variation_id", ""); // Reset variation when service changes
    }
  };

  const renderFormFields = () => {
    if (category === "airtime") {
      return (
        <>
          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="service_id" className="text-sm text-white/40">
              Service Provider
            </label>
            <div className="relative flex items-center justify-between transition-all duration-200 w-full overflow-hidden bg-gray-600 border border-primary/40 rounded-lg text-sm text-white/70 placeholder-white/40 focus:outline-none focus:border-primary">
              <select
                name="service_id"
                id="service_id"
                value={formik.values.service_id}
                onChange={handleServiceChange}
                className="bg-gray-600 px-4 py-3 appearance-none focus:outline-none w-full"
              >
                <option value="" disabled>
                  Select service provider
                </option>
                {AIRTIME_SERVICES.map((service) => (
                  <option key={service.value} value={service.value}>
                    {service.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="absolute top-4 right-3 text-white/60 text-base z-10 pointer-events-none" />
            </div>
            {formik.touched.service_id && formik.errors.service_id && (
              <p className="text-red-500 text-xs">{formik.errors.service_id}</p>
            )}
          </div>

          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="phone" className="text-sm text-white/40">
              Phone Number
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={formik.values.phone}
              onChange={handlePhoneChange}
              placeholder="+2348012345678 or 08012345678"
              className="form-input"
            />
            {formik.touched.phone && formik.errors.phone && (
              <p className="text-red-500 text-xs">{formik.errors.phone}</p>
            )}
          </div>

          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="amount" className="text-sm text-white/40">
              Amount (₦)
              {formik.values.service_id === "mtn" && (
                <span className="text-xs text-white/50 ml-2">
                  Minimum: ₦10
                </span>
              )}
              {formik.values.service_id &&
                formik.values.service_id !== "mtn" && (
                  <span className="text-xs text-white/50 ml-2">
                    Minimum: ₦50
                  </span>
                )}
            </label>
            <input
              id="amount"
              name="amount"
              type="number"
              step="1"
              min={formik.values.service_id === "mtn" ? 10 : 50}
              value={formik.values.amount || ""}
              onChange={formik.handleChange}
              placeholder="0"
              className="form-input spin-button-none"
            />
            {formik.touched.amount && formik.errors.amount && (
              <p className="text-red-500 text-xs">{formik.errors.amount}</p>
            )}
          </div>
        </>
      );
    }

    if (category === "data") {
      return (
        <>
          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="service_id" className="text-sm text-white/40">
              Service Provider
            </label>
            <div className="relative flex items-center justify-between transition-all duration-200 w-full overflow-hidden bg-gray-600 border border-primary/40 rounded-lg text-sm text-white/70 placeholder-white/40 focus:outline-none focus:border-primary">
              <select
                name="service_id"
                id="service_id"
                value={formik.values.service_id}
                onChange={handleServiceChange}
                className="bg-gray-600 px-4 py-3 appearance-none focus:outline-none w-full"
              >
                <option value="" disabled>
                  Select service provider
                </option>
                {DATA_SERVICES.map((service) => (
                  <option key={service.value} value={service.value}>
                    {service.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="absolute top-4 right-3 text-white/60 text-base z-10 pointer-events-none" />
            </div>
            {formik.touched.service_id && formik.errors.service_id && (
              <p className="text-red-500 text-xs">{formik.errors.service_id}</p>
            )}
          </div>

          {formik.values.service_id && (
            <div className="flex flex-col gap-2 mt-4">
              <label htmlFor="variation_id" className="text-sm text-white/40">
                Data Plan
              </label>
              <div className="relative flex items-center justify-between transition-all duration-200 w-full overflow-hidden bg-gray-600 border border-primary/40 rounded-lg text-sm text-white/70 placeholder-white/40 focus:outline-none focus:border-primary">
                <select
                  name="variation_id"
                  id="variation_id"
                  value={formik.values.variation_id}
                  onChange={formik.handleChange}
                  disabled={isLoadingVariations || !variationsData}
                  className="bg-gray-600 px-4 py-3 appearance-none focus:outline-none w-full disabled:opacity-50"
                >
                  <option value="" disabled>
                    {isLoadingVariations
                      ? "Loading plans..."
                      : variationsError
                        ? "Error loading plans"
                        : "Select data plan"}
                  </option>
                  {variationsData?.data
                    ?.filter((v) => v.availability === "Available")
                    .map((variation) => (
                      <option
                        key={variation.variation_id}
                        value={variation.variation_id.toString()}
                      >
                        {variation.data_plan} - ₦
                        {parseFloat(variation.price).toLocaleString()}
                      </option>
                    ))}
                </select>
                <FiChevronDown className="absolute top-4 right-3 text-white/60 text-base z-10 pointer-events-none" />
              </div>
              {formik.touched.variation_id && formik.errors.variation_id && (
                <p className="text-red-500 text-xs">
                  {formik.errors.variation_id}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="phone" className="text-sm text-white/40">
              Phone Number
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={formik.values.phone}
              onChange={handlePhoneChange}
              placeholder="+2348012345678 or 08012345678"
              className="form-input"
            />
            {formik.touched.phone && formik.errors.phone && (
              <p className="text-red-500 text-xs">{formik.errors.phone}</p>
            )}
          </div>
        </>
      );
    }

    if (category === "electricity") {
      return (
        <>
          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="service_id" className="text-sm text-white/40">
              Electricity Provider
            </label>
            <div className="relative flex items-center justify-between transition-all duration-200 w-full overflow-hidden bg-gray-600 border border-primary/40 rounded-lg text-sm text-white/70 placeholder-white/40 focus:outline-none focus:border-primary">
              <select
                name="service_id"
                id="service_id"
                value={formik.values.service_id}
                onChange={handleServiceChange}
                className="bg-gray-600 px-4 py-3 appearance-none focus:outline-none w-full"
              >
                <option value="" disabled>
                  Select electricity provider
                </option>
                {ELECTRICITY_SERVICES.map((service) => (
                  <option key={service.value} value={service.value}>
                    {service.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="absolute top-4 right-3 text-white/60 text-base z-10 pointer-events-none" />
            </div>
            {formik.touched.service_id && formik.errors.service_id && (
              <p className="text-red-500 text-xs">{formik.errors.service_id}</p>
            )}
          </div>

          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="variation_id" className="text-sm text-white/40">
              Meter Type
            </label>
            <div className="relative flex items-center justify-between transition-all duration-200 w-full overflow-hidden bg-gray-600 border border-primary/40 rounded-lg text-sm text-white/70 placeholder-white/40 focus:outline-none focus:border-primary">
              <select
                name="variation_id"
                id="variation_id"
                value={formik.values.variation_id}
                onChange={formik.handleChange}
                className="bg-gray-600 px-4 py-3 appearance-none focus:outline-none w-full"
              >
                <option value="" disabled>
                  Select meter type
                </option>
                {METER_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="absolute top-4 right-3 text-white/60 text-base z-10 pointer-events-none" />
            </div>
            {formik.touched.variation_id && formik.errors.variation_id && (
              <p className="text-red-500 text-xs">
                {formik.errors.variation_id}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="customer_id" className="text-sm text-white/40">
              Meter Number
            </label>
            <input
              id="customer_id"
              name="customer_id"
              type="text"
              value={formik.values.customer_id}
              onChange={formik.handleChange}
              placeholder="Enter meter number"
              className="form-input"
            />
            {formik.touched.customer_id && formik.errors.customer_id && (
              <p className="text-red-500 text-xs">
                {formik.errors.customer_id}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="amount" className="text-sm text-white/40">
              Amount (₦)
              <span className="text-xs text-white/50 ml-2">Minimum: ₦100</span>
            </label>
            <input
              id="amount"
              name="amount"
              type="number"
              step="1"
              min={100}
              value={formik.values.amount || ""}
              onChange={formik.handleChange}
              placeholder="0"
              className="form-input spin-button-none"
            />
            {formik.touched.amount && formik.errors.amount && (
              <p className="text-red-500 text-xs">{formik.errors.amount}</p>
            )}
          </div>
        </>
      );
    }

    if (category === "tv") {
      return (
        <>
          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="service_id" className="text-sm text-white/40">
              Service Provider
            </label>
            <div className="relative flex items-center justify-between transition-all duration-200 w-full overflow-hidden bg-gray-600 border border-primary/40 rounded-lg text-sm text-white/70 placeholder-white/40 focus:outline-none focus:border-primary">
              <select
                name="service_id"
                id="service_id"
                value={formik.values.service_id}
                onChange={handleServiceChange}
                className="bg-gray-600 px-4 py-3 appearance-none focus:outline-none w-full"
              >
                <option value="" disabled>
                  Select service provider
                </option>
                {TV_SERVICES.map((service) => (
                  <option key={service.value} value={service.value}>
                    {service.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="absolute top-4 right-3 text-white/60 text-base z-10 pointer-events-none" />
            </div>
            {formik.touched.service_id && formik.errors.service_id && (
              <p className="text-red-500 text-xs">{formik.errors.service_id}</p>
            )}
          </div>

          {formik.values.service_id && (
            <div className="flex flex-col gap-2 mt-4">
              <label htmlFor="variation_id" className="text-sm text-white/40">
                Package
              </label>
              <div className="relative flex items-center justify-between transition-all duration-200 w-full overflow-hidden bg-gray-600 border border-primary/40 rounded-lg text-sm text-white/70 placeholder-white/40 focus:outline-none focus:border-primary">
                <select
                  name="variation_id"
                  id="variation_id"
                  value={formik.values.variation_id}
                  onChange={formik.handleChange}
                  disabled={isLoadingTVVariations || !tvVariationsData}
                  className="bg-gray-600 px-4 py-3 appearance-none focus:outline-none w-full disabled:opacity-50"
                >
                  <option value="" disabled>
                    {isLoadingTVVariations
                      ? "Loading packages..."
                      : tvVariationsError
                        ? "Error loading packages"
                        : "Select package"}
                  </option>
                  {tvVariationsData?.data
                    ?.filter((v) => v.availability === "Available")
                    .filter(
                      (v) => v.service_id === formik.values.service_id
                    )
                    .map((variation) => (
                      <option
                        key={variation.variation_id}
                        value={variation.variation_id.toString()}
                      >
                        {variation.package_bouquet} - ₦
                        {parseFloat(variation.price).toLocaleString()}
                      </option>
                    ))}
                </select>
                <FiChevronDown className="absolute top-4 right-3 text-white/60 text-base z-10 pointer-events-none" />
              </div>
              {formik.touched.variation_id && formik.errors.variation_id && (
                <p className="text-red-500 text-xs">
                  {formik.errors.variation_id}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="customer_id" className="text-sm text-white/40">
              Customer ID (Smartcard/IUC)
            </label>
            <input
              id="customer_id"
              name="customer_id"
              type="text"
              value={formik.values.customer_id}
              onChange={formik.handleChange}
              placeholder="Enter Customer ID"
              className="form-input"
            />
            {formik.touched.customer_id && formik.errors.customer_id && (
              <p className="text-red-500 text-xs">
                {formik.errors.customer_id}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="subscription_type" className="text-sm text-white/40">
              Subscription Type (Optional)
            </label>
            <div className="relative flex items-center justify-between transition-all duration-200 w-full overflow-hidden bg-gray-600 border border-primary/40 rounded-lg text-sm text-white/70 placeholder-white/40 focus:outline-none focus:border-primary">
              <select
                name="subscription_type"
                id="subscription_type"
                value={formik.values.subscription_type}
                onChange={formik.handleChange}
                className="bg-gray-600 px-4 py-3 appearance-none focus:outline-none w-full"
              >
                {SUBSCRIPTION_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="absolute top-4 right-3 text-white/60 text-base z-10 pointer-events-none" />
            </div>
          </div>

          {formik.values.subscription_type === "renew" && (
            <div className="flex flex-col gap-2 mt-4">
              <label htmlFor="tv_amount" className="text-sm text-white/40">
                Renewal Amount (₦)
              </label>
              <input
                id="tv_amount"
                name="tv_amount"
                type="number"
                step="1"
                min={1}
                value={formik.values.tv_amount || ""}
                onChange={formik.handleChange}
                placeholder="Enter renewal amount"
                className="form-input spin-button-none"
              />
              {formik.touched.tv_amount && formik.errors.tv_amount && (
                <p className="text-red-500 text-xs">
                  {formik.errors.tv_amount}
                </p>
              )}
            </div>
          )}
        </>
      );
    }

    if (category === "epins") {
      return (
        <>
          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="service_id" className="text-sm text-white/40">
              Service Provider
            </label>
            <div className="relative flex items-center justify-between transition-all duration-200 w-full overflow-hidden bg-gray-600 border border-primary/40 rounded-lg text-sm text-white/70 placeholder-white/40 focus:outline-none focus:border-primary">
              <select
                name="service_id"
                id="service_id"
                value={formik.values.service_id}
                onChange={handleServiceChange}
                className="bg-gray-600 px-4 py-3 appearance-none focus:outline-none w-full"
              >
                <option value="" disabled>
                  Select service provider
                </option>
                {EPINS_SERVICES.map((service) => (
                  <option key={service.value} value={service.value}>
                    {service.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="absolute top-4 right-3 text-white/60 text-base z-10 pointer-events-none" />
            </div>
            {formik.touched.service_id && formik.errors.service_id && (
              <p className="text-red-500 text-xs">{formik.errors.service_id}</p>
            )}
          </div>

          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="value" className="text-sm text-white/40">
              Value (₦)
            </label>
            <div className="relative flex items-center justify-between transition-all duration-200 w-full overflow-hidden bg-gray-600 border border-primary/40 rounded-lg text-sm text-white/70 placeholder-white/40 focus:outline-none focus:border-primary">
              <select
                name="value"
                id="value"
                value={formik.values.value}
                onChange={formik.handleChange}
                className="bg-gray-600 px-4 py-3 appearance-none focus:outline-none w-full"
              >
                {EPINS_VALUES.map((val) => (
                  <option key={val} value={val}>
                    ₦{val}
                  </option>
                ))}
              </select>
              <FiChevronDown className="absolute top-4 right-3 text-white/60 text-base z-10 pointer-events-none" />
            </div>
            {formik.touched.value && formik.errors.value && (
              <p className="text-red-500 text-xs">{formik.errors.value}</p>
            )}
          </div>

          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="quantity" className="text-sm text-white/40">
              Quantity
              <span className="text-xs text-white/50 ml-2">
                Min: 1, Max: 40
              </span>
            </label>
            <input
              id="quantity"
              name="quantity"
              type="number"
              step="1"
              min={1}
              max={40}
              value={formik.values.quantity || ""}
              onChange={formik.handleChange}
              placeholder="1"
              className="form-input spin-button-none"
            />
            {formik.touched.quantity && formik.errors.quantity && (
              <p className="text-red-500 text-xs">
                {formik.errors.quantity}
              </p>
            )}
          </div>
        </>
      );
    }

    if (category === "betting") {
      return (
        <>
          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="service_id" className="text-sm text-white/40">
              Betting Provider
            </label>
            <div className="relative flex items-center justify-between transition-all duration-200 w-full overflow-hidden bg-gray-600 border border-primary/40 rounded-lg text-sm text-white/70 placeholder-white/40 focus:outline-none focus:border-primary">
              <select
                name="service_id"
                id="service_id"
                value={formik.values.service_id}
                onChange={handleServiceChange}
                className="bg-gray-600 px-4 py-3 appearance-none focus:outline-none w-full"
              >
                <option value="" disabled>
                  Select betting provider
                </option>
                {BETTING_SERVICES.map((service) => (
                  <option key={service.value} value={service.value}>
                    {service.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="absolute top-4 right-3 text-white/60 text-base z-10 pointer-events-none" />
            </div>
            {formik.touched.service_id && formik.errors.service_id && (
              <p className="text-red-500 text-xs">{formik.errors.service_id}</p>
            )}
          </div>

          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="customer_id" className="text-sm text-white/40">
              Customer ID
            </label>
            <input
              id="customer_id"
              name="customer_id"
              type="text"
              value={formik.values.customer_id}
              onChange={formik.handleChange}
              placeholder="Enter customer ID"
              className="form-input"
            />
            {formik.touched.customer_id && formik.errors.customer_id && (
              <p className="text-red-500 text-xs">
                {formik.errors.customer_id}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="amount" className="text-sm text-white/40">
              Amount (₦)
              <span className="text-xs text-white/50 ml-2">
                Min: ₦100, Max: ₦100,000
              </span>
            </label>
            <input
              id="amount"
              name="amount"
              type="number"
              step="1"
              min={100}
              max={100000}
              value={formik.values.amount || ""}
              onChange={formik.handleChange}
              placeholder="0"
              className="form-input spin-button-none"
            />
            {formik.touched.amount && formik.errors.amount && (
              <p className="text-red-500 text-xs">{formik.errors.amount}</p>
            )}
          </div>
        </>
      );
    }

    // Placeholder for other categories
    return (
      <div className="text-center py-8">
        <p className="text-white/60 text-sm">
          {name} form will be available soon
        </p>
      </div>
    );
  };

  return (
    <Transition appear show={isFormModalOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => setIsFormModalOpen(false)}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative font-dashboard w-full max-w-3xl min-h-[500px] transform overflow-hidden rounded-2xl bg-[#161819] px-4 pt-4 pb-10 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between px-4 pt-2 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="box-content p-2.5 rounded-full bg-primary">
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 28 28"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M20.8333 4.66667H4.16667C3.05208 4.66667 2.08333 5.63542 2.08333 6.75V19.25C2.08333 20.3646 3.05208 21.3333 4.16667 21.3333H20.8333C21.9479 21.3333 22.9167 20.3646 22.9167 19.25V6.75C22.9167 5.63542 21.9479 4.66667 20.8333 4.66667Z"
                          stroke="#343434"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M7.29167 9.33333H17.7083"
                          stroke="#343434"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M7.29167 13.5H17.7083"
                          stroke="#343434"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M7.29167 17.6667H12.5"
                          stroke="#343434"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>

                    <p className="text-base text-white/40 font-light">
                      {name}
                    </p>
                  </div>

                  <IoMdClose
                    className="text-xl text-white cursor-pointer"
                    onClick={() => setIsFormModalOpen(false)}
                  />
                </div>

                <hr className="border-white/10" />

                <form
                  onSubmit={formik.handleSubmit}
                  className="max-w-md mx-auto mt-8"
                >
                  {renderFormFields()}

                  {/* Preview Section */}
                  {previewData && (
                    <div className="w-full bg-gray-700 rounded-md border-2 border-dashed border-white/10 px-6 py-4 mt-6 flex flex-col gap-3">
                      <h4 className="text-white font-medium text-sm">
                        Preview
                      </h4>
                      <div className="flex flex-col gap-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-white/60">Product:</span>
                          <span className="text-white capitalize">
                            {previewData.product}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/60">Service:</span>
                          <span className="text-white capitalize">
                            {previewData.serviceName}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/60">Base Amount:</span>
                          <span className="text-white">
                            ₦{previewData.base.toLocaleString()}
                          </span>
                        </div>
                        {previewData.fee > 0 && (
                          <div className="flex justify-between">
                            <span className="text-white/60">Fee:</span>
                            <span className="text-white">
                              ₦{previewData.fee.toLocaleString()}
                            </span>
                          </div>
                        )}
                        <hr className="border-white/10 my-1" />
                        <div className="flex justify-between">
                          <span className="text-white font-medium">
                            Total:
                          </span>
                          <span className="text-white font-bold text-lg">
                            ₦{previewData.total.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          {previewData.sufficient ? (
                            <span className="text-green-400 text-xs flex items-center gap-1">
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Sufficient balance
                            </span>
                          ) : (
                            <span className="text-red-400 text-xs flex items-center gap-1">
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Insufficient balance
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {(category === "airtime" ||
                    category === "data" ||
                    category === "electricity" ||
                    category === "tv" ||
                    category === "epins" ||
                    category === "betting") && (
                      <>
                        <div className="flex items-center gap-2 mt-4">
                          <input
                            type="checkbox"
                            name="acceptTerms"
                            id="acceptTerms"
                            checked={formik.values.acceptTerms}
                            onChange={formik.handleChange}
                            className="form-checkbox"
                          />
                          <label
                            htmlFor="acceptTerms"
                            className="text-sm text-white/70"
                          >
                            I agree to the{" "}
                            <a
                              target="_blank"
                              href="https://gcbuying.com/terms"
                              className="text-white/100 underline hover:text-white/60 transition-all duration-200"
                            >
                              Terms and Conditions
                            </a>{" "}
                            &{" "}
                            <a
                              target="_blank"
                              href="https://gcbuying.com/privacy"
                              className="text-white/100 underline hover:text-white/60 transition-all duration-200"
                            >
                              Privacy Policy
                            </a>
                          </label>
                        </div>
                        {formik.touched.acceptTerms &&
                          formik.errors.acceptTerms && (
                            <p className="text-red-500 text-xs mt-1">
                              {formik.errors.acceptTerms}
                            </p>
                          )}

                        <button
                          type="submit"
                          disabled={isPreviewing || !!previewData}
                          className="btn-primary px-4 py-2 rounded-md mt-6 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isPreviewing ? "Previewing..." : "Preview"}
                        </button>

                        {previewData && (
                          <button
                            type="button"
                            onClick={handleConfirm}
                            disabled={isConfirming}
                            className="btn-primary px-4 py-2 rounded-md mt-3 w-full disabled:opacity-50 disabled:cursor-not-allowed bg-green-600 hover:bg-green-700"
                          >
                            {isConfirming ? "Confirming..." : "Confirm"}
                          </button>
                        )}
                      </>
                    )}
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
